import { BookOpen, Check, FileCode2, HelpCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';

import { Activity, ActivityKind } from '../../data/programs';
import { savannaAuthorizeUrl } from '../../data/savannaCurriculum';

/**
 * Savanna's three activity types, given an icon each. `other` gets none: an
 * unrecognised type is better shown plain than labelled with a guess.
 */
const KIND_ICON: Record<Exclude<ActivityKind, 'other'>, typeof BookOpen> = {
  concept: BookOpen,
  project: FileCode2,
  quiz: HelpCircle,
};

interface Props {
  activity: Activity;
  /** Whether paid activities are currently openable (a covering subscription). */
  paidUnlocked?: boolean;
  /** The course's Savanna curriculum id, for the `/ehub/authorize` link. */
  curriculumId: string;
}

/**
 * One activity: the leaf of the interactive tree, and the thing a learner
 * actually opens.
 *
 * The action reads "Continue" once the activity has been passed and "Start"
 * before, matching Figma 12154:58175. Savanna reports only `isPassed`, so
 * there is no in-progress state to distinguish - a started-but-unfinished
 * activity is indistinguishable from an untouched one and says "Start".
 *
 * A paywalled activity with no covering subscription shows a lock instead of
 * a link. Savanna enforces the same boundary server-side, reading the
 * learner's subscription from the viewer; this keeps the page from offering a
 * door that would be shut on arrival.
 */
export const ActivityRow = ({
  activity,
  paidUnlocked = false,
  curriculumId,
}: Props) => {
  const Icon = activity.kind === 'other' ? undefined : KIND_ICON[activity.kind];
  const locked = activity.paid && !paidUnlocked;
  /*
   * Every activity link goes through eHub's own SSO handshake rather than
   * straight to Savanna's origin - the same pattern `LmsClassLocation.courseLink`
   * already uses on the backend - because a raw Savanna link leaves the
   * learner logged out there. `activity.path` (Savanna's `pathForLearner`)
   * is passed as `redirect_to`, landing the learner on this exact activity
   * once authenticated instead of the curriculum root. See
   * `savannaAuthorizeUrl` for the curriculum-only fallback when it's absent.
   */
  const authorizeUrl = process.env['NX_SAVANNA_AUTHORIZE_URL'];
  const href = authorizeUrl
    ? savannaAuthorizeUrl(authorizeUrl, curriculumId, activity.path)
    : undefined;

  return (
    <div className="flex items-center gap-3 py-2 text-sm">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          locked ? 'text-muted-foreground' : 'text-foreground'
        )}
      >
        {activity.title}
      </span>

      {activity.completed && (
        <Check
          className="h-4 w-4 shrink-0 text-primary"
          aria-label="Completed"
        />
      )}

      {locked ? (
        <>
          <Lock
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-label="Needs a subscription"
          />
          <span className="shrink-0 rounded-full border border-saffron/30 bg-saffron/10 px-2.5 py-0.5 text-xs font-medium text-saffron-active">
            Paid
          </span>
        </>
      ) : (
        href && (
          <Button
            asChild
            variant="outline"
            className="h-8 shrink-0 rounded-full border-[1.5px] border-primary px-5 text-primary hover:bg-primary/10 hover:text-primary"
          >
            <Link to={href} target="_blank" rel="noopener noreferrer">
              {activity.completed ? 'Continue' : 'Start'}
            </Link>
          </Button>
        )
      )}
    </div>
  );
};
